import React, { useEffect, useState, useCallback } from "react";
import { Accordion, AccordionSummary } from "@material-ui/core";
import { getOrdersResultByDate, getOrdersResultByFullName } from "@cabinet-packages/api/api";
import { useSelector } from "react-redux";
import { CustomDialog, Loading, LoadOrderResultDialog } from "@cabinet-packages/components";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AssayTable from "@cabinet-packages/pages/AssayTable";
import CustomButton from "../../components/Button";
import moment from "moment";
import { useStyles } from "./styles";
import { designTokens } from "../../styles/designTokens";
import PageHeader from "../../components/PageHeader";
import ScrollContainer from "../../components/ScrollContainer/ScrollContainer";

const SearchPatientResults = () => {
    const classes = useStyles();
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
    const [endDate, setEndDate] = useState(new Date());
    const { activePatient } = useSelector(state => state.patients);
    const [foundPatientsResults, setFoundPatientsResults] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [expanded, setExpanded] = useState({});
    const { themeMaterialUI } = useSelector(store => store.theme);

    const handleDateRangeChange = (newStartDate, newEndDate) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    };

    const handleSearchValueChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleSearch = async (event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (searchValue && searchValue.trim().length > 0) {
            await searchByName();
        } else {
            await searchByDate();
        }
    };

    const searchByDate = useCallback(async () => {
        if (!startDate || !endDate || !activePatient?.id) return;

        const dateFrom = startDate.toISOString().slice(0, 10);
        const dateTo = endDate.toISOString().slice(0, 10);
        setFoundPatientsResults(false);

        const result = await getOrdersResultByDate(activePatient.id, dateFrom, dateTo).catch(() => []);

        setFoundPatientsResults(result.reduce((acu, cur) => {
            if (+cur.ready === 0 && cur.idOrder < 2000000) return acu;
            if (acu.find(item => item.idOrder === cur.idOrder)) return acu;
            return [...acu, cur];
        }, []));
    }, [startDate, endDate, activePatient]);

    const searchByName = async () => {
        if (searchValue.length === 0 || !activePatient?.id) return;
        setFoundPatientsResults(false);

        const result = await getOrdersResultByFullName(activePatient.id, searchValue).catch(() => []);

        setFoundPatientsResults(result.reduce((acu, cur) => {
            if (+cur.ready === 0 && cur.idOrder < 2000000) return acu;
            if (acu.find(item => item.idOrder === cur.idOrder)) return acu;
            return [...acu, cur];
        }, []));
    };

    const sendOrder = (event, idOrder, idClient, open, patient) => {
        event.preventDefault();
        event.stopPropagation();

        CustomDialog(LoadOrderResultDialog,
            {
                open,
                activePatient: {
                    firstname: patient[1],
                    lastname: patient[0],
                    fathername: patient[2]
                },
                idOrder,
                idClient,
                themeMaterialUI
            },
            false,
            themeMaterialUI);
    };

    function handleClick(event, bool, idOrder) {
        event.stopPropagation();
        event.preventDefault();

        setExpanded(prevState => ({ ...prevState, [idOrder]: bool }));
    }

    useEffect(() => {
        if (foundPatientsResults && typeof foundPatientsResults !== "string" && foundPatientsResults.length > 0) {
            setExpanded(foundPatientsResults.reduce((acu, cur) => {
                return { ...acu, [cur.idOrder]: false };
            }, {}));
        }
    }, [foundPatientsResults]);

    useEffect(() => {
        if (searchValue) {
            setSearchValue('');
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (activePatient?.id) {
            searchByDate();
        }
    }, [activePatient?.id]);

    if (!activePatient) {
        return (
            <div className={classes.root}>
                <PageHeader
                    title="Пошук результатів"
                    subtitle="Оберіть пацієнта для перегляду результатів"
                />
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <PageHeader
                title="Пошук результатів"
                subtitle="Тут зберігається історія аналізів пацієнтів"
                searchInput={{
                    value: searchValue,
                    onChange: handleSearchValueChange,
                    onSearch: handleSearch,
                    placeholder: "Пошук за ПІБ пацієнта"
                }}
                dateRangePicker={{
                    initialStartDate: startDate,
                    initialEndDate: endDate,
                    onChange: handleDateRangeChange,
                    disableFuture: true,
                    placeholder: "Оберіть діапазон дат"
                }}
                button={{
                    variant: "primary",
                    onClick: handleSearch,
                    children: "Шукати результати",
                    size: "small"
                }}
            />

            {foundPatientsResults === false && <div className={classes.loadingWrapper}><Loading /></div>}

            {foundPatientsResults && foundPatientsResults.length === 0 && (
                <div className={classes.noResultsContainer}>
                    <div className={classes.noResults}>
                        <div className={classes.noResultsTitle}>Пошук не дав результатів</div>
                        <div className={classes.noResultsSubtitle}>Спробуйте змінити запит або перевірте написання</div>
                    </div>
                </div>
            )}

            {foundPatientsResults && typeof foundPatientsResults === "string" && (
                <div className={classes.noResultsContainer}>
                    <div className={classes.noResults}>
                        <div className={classes.noResultsTitle}>Вибачте, сталась помилка під час завантаження</div>
                        <div className={classes.noResultsSubtitle}>Спробуйте оновити сторінку або зверніться до підтримки</div>
                    </div>
                </div>
            )}

            {foundPatientsResults && typeof foundPatientsResults !== "string" && foundPatientsResults.length > 0 && Object.keys(expanded).length > 0 && (
                <ScrollContainer>
                    <div className={classes.resultsContainer}>
                        {foundPatientsResults.map((result) => {
                            const patient = result.fullname.split(' ');

                            return (
                                <Accordion
                                    key={result.idOrder}
                                    onChange={(event, bool) => {
                                        if (+result.ready !== 0) {
                                            handleClick(event, bool, result.idOrder);
                                        }
                                    }}
                                    expanded={expanded[result.idOrder] ?? false}
                                    className={classes.resultCard}
                                >
                                    <AccordionSummary expandIcon={+result.ready !== 0 ? <ExpandMoreIcon /> : null}>
                                        <div className={classes.summaryContainer}>
                                            <div className={classes.orderInfo}>Час забору: {moment(result.dt).format('DD.MM.YYYY HH:mm')}</div>
                                            <div className={classes.patientInfo}>{`Пацієнт: ${patient[0]} ${patient[1]} ${patient[2]}`}</div>
                                            <div>
                                                <span className={classes.orderInfo}>ID: </span>
                                                <span className={classes.patientInfo}>{result.idOrder}</span>
                                            </div>
                                            <div className={classes.actionsWrapper}>
                                                {+result.ready !== 0 ? (
                                                    <div
                                                        className={classes.buttonWrapper}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        <CustomButton
                                                            variant="secondary"
                                                            onClick={(event) => sendOrder(event, result.idOrder, result.idClient, false, patient)}
                                                            icon={<OpenInNewIcon />}
                                                            iconPosition="right"
                                                            size="small"
                                                        >
                                                            Відкрити PDF бланк
                                                        </CustomButton>
                                                    </div>
                                                ) : (
                                                    <div style={{ marginTop: '8px', color: designTokens.colors.text.secondary }}>
                                                        Результати по даному замовленню ще не готові.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <div className={classes.tablesContainer}>
                                        <AssayTable id={result.idOrder} patientId={activePatient.id} isDoctor={true} isExpanded={expanded[result.idOrder] ?? false} />
                                    </div>
                                </Accordion>
                            );
                        })}
                    </div>
                </ScrollContainer>
            )}
        </div>
    );
};

export default SearchPatientResults;
